// English strings for the console batch 1 (SUPPORT-001 / SYSTEM-001, owner 2026-10-25; D-067): the
// support desk, help guides, shops' ideas, What's new, the onboarding tracker and the shop health
// score, plus the shop's side (Help centre → Ideas, What's new). Spread into `en` as `en.desk`, so
// keys read `t("desk.…")`. Copy rules: DESIGN_SYSTEM §9.6.
export const deskStrings = {
  health: {
    title: "Health",
    score: "{score}/100",
    bands: { healthy: "Healthy", watch: "Watch", at_risk: "At risk", none: "Paused" },
    parts: {
      activity: "Activity",
      sales: "Sales trend",
      setup: "Setup",
      billing: "Billing",
      support: "Support",
    },
    reasons: {
      inactive: "No sign-in for 14+ days",
      quiet: "Quiet for a week",
      no_orders: "No orders in 4 weeks",
      orders_down: "Orders going down",
      setup_low: "Setup not finished",
      grace: "Plan in grace",
      renewal_short: "Wallet short for renewal",
      renewal_off: "Renewal turned off",
      tickets_open: "Open ticket",
      tickets_overdue: "Ticket past reply time",
      paused: "Suspended or closed",
    },
    how: "Activity 30, sales trend 25, billing 20, setup 15, support 10. 70+ is healthy, under 40 at risk.",
    overview: "Shop health",
    lowest: "Needs a look",
    allHealthy: "Every shop looks healthy.",
    filterAtRisk: "See shops at risk",
  },
  setup: {
    lead: "Where every shop is in setting up. A nudge sends the shop a message that opens the step.",
    steps: {
      store_live: "Store live",
      products: "Products added",
      verified: "Business verified",
      domain: "Own domain",
      channels: "Channels connected",
      first_order: "First order",
      wallet: "Wallet topped up",
    },
    funnel: "Shops that finished each step",
    complete: "{n} of {total} shops finished setup",
    progress: "{n}/{total}",
    next: "Next: {step}",
    allDone: "All done",
    nudge: "Nudge",
    nudgeAll: { one: "Nudge {count} shop", other: "Nudge {count} shops" },
    nudged: "Nudged {date}",
    stuckOn: "Next step",
    anyStep: "Any step",
    unfinished: "Not finished",
    everyone: "All shops",
    nudgeTitle: "Send a nudge",
    nudgeFor: {
      one: "To {count} shop. The message opens: {step}.",
      other: "To {count} shops. The message opens: {step}.",
    },
    messageTitle: "Title",
    messageBody: "Message",
    send: "Send nudge",
    sent: { one: "Nudge sent to {count} shop", other: "Nudge sent to {count} shops" },
    none: "No shops match.",
    joined: "Joined {date}",
    templates: {
      store_live: {
        title: "Your store is almost ready",
        body: "Publish your online store so customers can start ordering. Open Online store and press Publish.",
      },
      products: {
        title: "Add your first products",
        body: "Shops with 10 or more products get their first order much sooner. Add a few with clear photos and prices.",
      },
      verified: {
        title: "Verify your business",
        body: "Verify your business to build trust with customers. Open Settings → Organization → Business verification.",
      },
      domain: {
        title: "Connect your own domain",
        body: "Use your own .com or .com.np so customers find you easily. Open Online store → Domains.",
      },
      channels: {
        title: "Connect Facebook, Instagram or WhatsApp",
        body: "Answer every customer message in one place with Sampark. Connect a channel to start.",
      },
      first_order: {
        title: "Get your first order",
        body: "Share a checkout link on Facebook, Instagram or TikTok, and customers can order in one tap.",
      },
      wallet: {
        title: "Top up your Adyvance Wallet",
        body: "Your wallet pays for SMS, root AI credits, premium themes and plans. Top up to use them.",
      },
    },
  },
  desk: {
    lead: "Shops' Help centre tickets. Replies show in the shop's Help centre; internal notes never do.",
    tabs: { inbox: "Inbox", guides: "Help guides" },
    views: {
      active: "Waiting",
      mine: "Mine",
      unassigned: "Unassigned",
      overdue: "Past reply time",
      closed: "Closed",
    },
    states: { open: "Open", answered: "Answered", closed: "Closed" },
    priorities: { low: "Low", normal: "Normal", high: "High" },
    categories: { question: "Question", problem: "Problem", billing: "Billing", feature: "Idea" },
    search: "Search subject or shop",
    none: "Nothing here.",
    pick: "Pick a ticket to read it.",
    overdue: "Past reply time",
    dueBy: "Reply by {time}",
    shopWaiting: "Shop waiting",
    weReplied: "We replied",
    assignee: "Assigned to",
    nobody: "Nobody",
    takeIt: "Take it",
    priority: "Priority",
    reply: "Reply",
    note: "Internal note",
    replyPh: "Write to the shop…",
    notePh: "Only the console team sees this…",
    send: "Send reply",
    sendClose: "Send and close",
    addNote: "Add note",
    close: "Close",
    reopen: "Reopen",
    toIdea: "Save as idea",
    savedIdea: "Saved as an idea",
    internal: "Internal note",
    shopAuthor: "Shop",
    staffAuthor: "Adyvance",
    page: "Page {page}",
    store: "Store {name}",
    openShop: "Open shop",
    otherTickets: "Other tickets from this shop",
    noOthers: "No other tickets.",
    snippets: "Saved replies",
    snippet: {
      looking: {
        label: "Looking into it",
        body: "Thanks for writing to us. We're looking into this now and will reply here soon.",
      },
      fixed: {
        label: "Fixed",
        body: "This is fixed now. Please refresh the page and try again. Reply here if you still see the problem.",
      },
      guide: {
        label: "See the guide",
        body: "The Help centre has a guide with each step for this. Reply here if anything is unclear.",
      },
      idea: {
        label: "Added to ideas",
        body: "Thanks for the idea! We've added it to our list. You can follow it and vote under Help centre → Ideas.",
      },
    },
    replied: "Reply sent",
    noted: "Note added",
    updated: "Ticket updated",
    messages: { one: "{count} message", other: "{count} messages" },
    opened: "Opened {date}",
  },
  guides: {
    lead: "Guides shops read in their Help centre. Only published guides show.",
    new: "New guide",
    edit: "Edit guide",
    title: "Title",
    slug: "Address",
    slugHelp: "Lowercase letters, numbers and dashes, like add-products.",
    topic: "Topic",
    video: "Video link",
    videoHelp: "Optional. An https link (YouTube works).",
    body: "Guide",
    bodyHelp:
      "Plain text. Leave a blank line between paragraphs; start lines with 1. 2. 3. for steps.",
    published: "Published",
    draft: "Draft",
    publish: "Shops can read it",
    save: "Save guide",
    delete: "Delete",
    deleteConfirm: "Delete this guide? Shops will no longer see it.",
    saved: "Guide saved",
    deleted: "Guide deleted",
    none: "No guides yet.",
    search: "Search guides",
    count: { one: "{count} guide", other: "{count} guides" },
    updated: "Updated {date}",
  },
  ideas: {
    lead: "Ideas shops sent and voted for. Make one public so every shop can vote; the sender's name is never shown to other shops.",
    statuses: {
      new: "New",
      under_review: "Under review",
      planned: "Planned",
      in_progress: "In progress",
      shipped: "Shipped",
      declined: "Declined",
    },
    all: "All",
    votes: { one: "{count} vote", other: "{count} votes" },
    paying: "{n} paying",
    public: "Public",
    private: "Only the sender",
    from: "From {name}",
    fromStaff: "Added by Adyvance",
    new: "New idea",
    edit: "Idea",
    titleLabel: "Title",
    bodyLabel: "Details",
    status: "Status",
    publicLabel: "Every shop can see and vote",
    publicHelp: "Check the wording first: other shops will read it.",
    response: "Reply to shops",
    responseHelp: "Shown under the idea in every shop that can see it.",
    registry: "Registry ID",
    registryHelp: "The FEATURE_REGISTRY ID it belongs to, like PAYMENT-001.",
    merge: "Merge into",
    mergeHelp: "A duplicate: its votes move to the idea you pick, and it disappears from shops.",
    mergePick: "Pick an idea",
    mergeDo: "Merge",
    merged: "Merged into {title}",
    mergedToast: "Ideas merged",
    voters: "Voted by",
    noVoters: "No votes yet.",
    none: "No ideas here.",
    saved: "Idea saved",
    release: "Shipped in {title}",
    sortVotes: "Most votes",
    sortRecent: "Recent",
    search: "Search ideas",
    save: "Save",
  },
  releases: {
    lead: "What's new: notes every shop sees in its Help centre, with a dot on the bell until they read them.",
    tabs: { whatsNew: "What's new", deploys: "Deployments" },
    deploysTitle: "Deployments",
    deploysBody:
      "Versions per environment, promoting staging to production and rollbacks arrive with the release tooling (Phase 8).",
    new: "New note",
    edit: "Edit note",
    title: "Title",
    body: "What changed",
    tag: "Kind",
    tags: { new: "New", improved: "Improved", fixed: "Fixed" },
    link: "Link",
    linkHelp: "An admin page like /products, or an https link.",
    feature: "Plan feature",
    featureHelp: "Shops whose plan lacks it see the plan that has it.",
    anyPlan: "Every plan",
    ideas: "Ideas it ships",
    ideasHelp: "Publishing marks them shipped.",
    notify: "Tell the shops that voted",
    notifyHelp: "Each shop that voted for a linked idea gets a message (once, on first publish).",
    published: "Published",
    draft: "Draft",
    publish: "Publish",
    saveDraft: "Save draft",
    unpublish: "Unpublish",
    seen: { one: "{count} person read it", other: "{count} people read it" },
    saved: "Note saved",
    publishedToast: "Published",
    told: { one: "Published. {count} shop told.", other: "Published. {count} shops told." },
    none: "No notes yet.",
    publishedOn: "Published {date}",
  },
  shop: {
    tabs: { help: "Guides and tickets", ideas: "Ideas", whatsNew: "What's new" },
    ideas: {
      lead: "Tell us what would help your shop, and vote for ideas from other shops.",
      new: "Suggest an idea",
      titlePh: "Let customers…",
      bodyPh: "What would it help you do? (optional)",
      send: "Send idea",
      sent: "Thanks! Your idea was sent.",
      vote: "Vote",
      voted: "Voted",
      votes: { one: "{count} vote", other: "{count} votes" },
      mine: "Your idea",
      review: "Only you see this until we review it.",
      reply: "Adyvance replied",
      shipped: "Shipped: {title}",
      none: "No ideas yet. Be the first!",
      closed: "Voting closed",
      filters: { all: "All", mine: "Mine", planned: "Planned", shipped: "Shipped" },
    },
    whatsNew: {
      lead: "New in Adyvance.",
      none: "Nothing new yet.",
      newBadge: "New",
      open: "Open",
      inPlan: "In {plan}",
      bell: "What's new",
      count: "{n} new",
    },
  },
} as const;
